import React, {createRef, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import config from '../config.js';
import UserContext from '../components/common/UserContext.js';

// components
import Spinner from '../components/common/Spinner.js';
import PageHeader from '../components/common/PageHeader.js';
import HostConfigGrid from '../components/HostConfig/HostConfigGrid.js';
import {useSupportContext} from "../components/common/SupportContext";

// kendo react
import {Button} from '@progress/kendo-react-buttons';
import {ExcelExport, ExcelExportColumn} from '@progress/kendo-react-excel-export';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {Text} from '../components/common/MultilingualText.js';
import {
    serialNumberKey,
    exportHostInfoKey,
    aliasKey,
    assetInfoKey,
    cityKey,
    countryKey,
    hostIdKey,
    notesKey,
    stateProvinceKey,
    manageHostsKey,
    myHostsKey,
    mainMessages
} from '../assets/text/MultilingualText.js';
import {Tooltip} from "@progress/kendo-react-tooltip";
import {fileExcelIcon} from "@progress/kendo-svg-icons";

function HostConfig(props) {
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);
    const { dispatch } = useSupportContext();
    const localization = useLocalization();

    const [isLoading, setIsLoading] = useState(false);

    const [hosts, setHosts] = useState([]);

    // kendo react excel export
    const [userExcelData, setUserExcelData] = useState([]);
    const [excelFileName, setExcelFileName] = useState("");

    const _exporter = createRef();
    const exportExcel = () => {
        if (_exporter.current) {
            _exporter.current.save();
        }
    };

    /*
    * getHosts() gets all hosts
   */
    const getHosts = () => {
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        setIsLoading(true);

        axios.get(
            `${config.host_config.HOSTS}`,
            {headers: headers}
        )
            .then((response) => {
                if (response.status === 200) {
                    let data = response.data.user_hosts_list || [];

                    setIsLoading(false);
                    setHosts(data);
                    setUserExcelData(data);

                    var today = new Date(),
                        date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
                    setExcelFileName('KSM_HOSTS_' + date + '.xlsx');
                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to GET Hosts", error);
                setIsLoading(false);
            });
    }

    // initialize my hosts grid
    useEffect(() => {
        getHosts();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Support Context Metadata
    // Set Module and Step values on page load
    useEffect(() => {
        dispatch({ type: 'MODULE_LOAD', payload: {module: 'Manage Hosts', step: ''}});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {isLoading ? <Spinner/> : <></>}
            <PageHeader/>
            <div className={"ksm-page-container"}>

                <div className={"k-h3"}>
                    <Text
                        textkey={manageHostsKey}
                        textdefault={mainMessages[siteLanguageDefault][manageHostsKey]}
                    />
                </div>
                <div
                    className={'k-mb-2'}
                    style={{
                        display: "flex",
                        justifyContent: 'space-between',
                        alignItems: 'end',
                    }}
                >
                    <div
                        style={{marginBottom: "0"}}
                        className={"k-h5"}
                    >
                        <Text
                            textkey={myHostsKey}
                            textdefault={mainMessages[siteLanguageDefault][myHostsKey]}
                        />
                    </div>
                    <Tooltip
                        anchorElement="target"
                        showCallout={true}
                        parentTitle={true}
                        openDelay={0}
                        position="left"
                    >
                        <Button
                            title={localization.toLanguageString(exportHostInfoKey, mainMessages[siteLanguageDefault][exportHostInfoKey])}
                            themeColor={"tertiary"}
                            fillMode={"solid"}
                            svgIcon={fileExcelIcon}
                            onClick={exportExcel}
                            disabled={hosts.length === 0}
                        />
                    </Tooltip>
                </div>
                <div>
                    <HostConfigGrid
                        hosts={hosts}
                        getHosts={getHosts}
                        setIsLoading={setIsLoading}
                    />
                </div>
                <ExcelExport
                    data={userExcelData}
                    fileName={excelFileName}
                    ref={_exporter}
                >
                    <ExcelExportColumn
                        field="host_id"
                        title={localization.toLanguageString(hostIdKey, mainMessages[siteLanguageDefault][hostIdKey])}
                    />
                    <ExcelExportColumn
                        field="serial_number"
                        title={localization.toLanguageString(serialNumberKey, mainMessages[siteLanguageDefault][serialNumberKey])}
                    />
                    <ExcelExportColumn
                        field="alias"
                        title={localization.toLanguageString(aliasKey, mainMessages[siteLanguageDefault][aliasKey])}
                    />
                    <ExcelExportColumn
                        field="asset_info"
                        title={localization.toLanguageString(assetInfoKey, mainMessages[siteLanguageDefault][assetInfoKey])}
                    />
                    <ExcelExportColumn
                        field="city"
                        title={localization.toLanguageString(cityKey, mainMessages[siteLanguageDefault][cityKey])}
                    />
                    <ExcelExportColumn
                        field="state"
                        title={localization.toLanguageString(stateProvinceKey, mainMessages[siteLanguageDefault][stateProvinceKey])}
                    />
                    <ExcelExportColumn
                        field="country"
                        title={localization.toLanguageString(countryKey, mainMessages[siteLanguageDefault][countryKey])}
                    />
                    <ExcelExportColumn
                        field="notes"
                        title={localization.toLanguageString(notesKey, mainMessages[siteLanguageDefault][notesKey])}
                    />
                </ExcelExport>
            </div>
        </>
    );
}

export default HostConfig;
